import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PermissionString } from '../constants/permissions';
import { APP_ROUTES, CHAT_SESSION_ID_QUERY_PARAM_KEY } from '../constants/routes';
import { HttpCodes } from '../enums/httpCodes';
import { HaygentChatState, HaygentMessage, HaygentSession } from '../interfaces';
import {
  cacheHaygentSessionMessages,
  refreshHaygentSessions,
  updateHaygentSessionMessages,
  updateHaygentSessionName,
  useHaygentSessionsQuery,
  useHaygentSessionMessagesQuery,
} from '../queries/haygent';
import { useImpersonatedUser } from '../queries/impersonation';
import { HaygentService } from '../services';
import { isEUprod, isStaging, isTest, isUKprod, isUSprod } from '../utils/isNotProd';
import { useHasAccess } from './useHasAccess';
import { useHasPermission } from './useHasPermission';
import { useFleet } from './useFleet';

const getInitialHaygentSession = (sessions: HaygentSession[], requestedSessionId: string | null): HaygentSession | undefined => {
  return sessions.find((session) => session.session_id === requestedSessionId) || sessions[0];
};

export interface HaygentAvailability {
  enabled: boolean;
  fullPageEnabled: boolean;
  widgetEnabled: boolean;
}

// TODO: Replace the inherited Fleet Health permission when a dedicated Haygent permission is available.
export const HAYGENT_PERMISSION: PermissionString = 'devices:read:status';

export const useHaygentAvailability = (pathname?: string): HaygentAvailability => {
  const { data: impersonatedUser } = useImpersonatedUser();
  const hasAccess = useHasAccess();
  const hasHaygentPermission = useHasPermission(HAYGENT_PERMISSION);
  const enabledForUser = !impersonatedUser && hasAccess && hasHaygentPermission;
  // EU pilot is intentionally excluded; do not add isEUpilot() to this environment allowlist.
  const enabledEnvironment = isTest() || isStaging() || isUSprod() || isEUprod() || isUKprod();
  const enabled = enabledForUser && enabledEnvironment;

  return {
    enabled,
    fullPageEnabled: enabled,
    widgetEnabled: enabled && Boolean(pathname) && pathname !== APP_ROUTES.chat,
  };
};

interface UseHaygentChatReturn {
  // State
  chatState: HaygentChatState;
  messages: HaygentMessage[];
  sessionTitle: string | null;
  isLoading: boolean;
  error: string | null;
  sessionId: string | null;
  hasActiveDraft: boolean;
  isHistoryUnavailable: boolean;
  availability: HaygentAvailability;

  // Actions
  openChat: () => void;
  closeChat: () => void;
  startNewChat: () => void;
  clearActiveSession: () => void;
  selectSession: (session: HaygentSession) => void;
  toggleMaximize: () => void;
  sendMessage: (content: string) => Promise<void>;
  cancelRequest: () => void;
}

/**
 * Custom hook for managing Haygent AI chat state and interactions.
 *
 * Handles:
 * - Chat window state (collapsed/docked/maximized)
 * - Message history
 * - SSE streaming responses from the AI
 * - Session management
 * - Request cancellation
 */
const HaygentChatContext = createContext<UseHaygentChatReturn | null>(null);

function useHaygentChatState(): UseHaygentChatReturn {
  const history = useHistory();
  const location = useLocation();
  const availability = useHaygentAvailability(location.pathname);
  const fleet = useFleet();
  const fleetUuid = fleet?.uuid;
  // Chat window display state: collapsed (FAB only), docked (small window), maximized (large overlay)
  const [chatState, setChatState] = useState<HaygentChatState>(HaygentChatState.Collapsed);
  // Title sent once by the backend during the first streamed response
  const [sessionTitle, setSessionTitle] = useState<string | null>(null);
  // Loading state for session creation and streaming. History loading is owned by React Query.
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  // User-facing error message
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [hasActiveDraft, setHasActiveDraft] = useState(false);
  const sessionsQuery = useHaygentSessionsQuery(availability.enabled);
  const messagesQuery = useHaygentSessionMessagesQuery(sessionId);
  const messages = messagesQuery.data ?? [];
  const isHistoryUnavailable = Boolean(sessionId && messagesQuery.isError);
  const isLoading = isRequestLoading || Boolean(sessionId && messagesQuery.isLoading);

  // Persistent session ID across re-renders (not in state to avoid re-renders)
  const sessionIdRef = useRef<string | null>(null);
  const didRestoreInitialSessionRef = useRef(false);
  // AbortController for cancelling ongoing SSE stream requests
  const abortControllerRef = useRef<AbortController | null>(null);
  // Monotonic generation guard: abort is best-effort, so stale async work must also prove it still owns the active session.
  const sessionSelectionRequestRef = useRef(0);

  const resetConversationState = useCallback((activateDraft = true) => {
    cacheHaygentSessionMessages(null, []);
    sessionIdRef.current = null;
    setSessionId(null);
    setHasActiveDraft(activateDraft);
    setSessionTitle(null);
    setError(null);
    setIsRequestLoading(false);
  }, []);

  useEffect(() => {
    if (messagesQuery.isError) {
      toast.error('Failed to load conversation history.');
    }
  }, [messagesQuery.errorUpdatedAt, messagesQuery.isError]);

  const updateSessionIdInUrl = useCallback(
    (nextSessionId: string | null) => {
      const params = new URLSearchParams(location.search);

      if (nextSessionId) {
        params.set(CHAT_SESSION_ID_QUERY_PARAM_KEY, nextSessionId);
      } else {
        params.delete(CHAT_SESSION_ID_QUERY_PARAM_KEY);
      }

      history.replace({ ...location, search: params.toString() });
    },
    [history, location],
  );

  /** Opens the chat widget without creating a backend session. */
  const openChat = () => {
    setError(null);
    setChatState(HaygentChatState.Docked);
  };

  /** Hides the widget while preserving the active conversation. */
  const closeChat = () => {
    setChatState(HaygentChatState.Collapsed);
  };

  const resetActiveConversation = useCallback(
    (activateDraft: boolean) => {
      // Invalidate pending history loads and streams before clearing the active conversation.
      sessionSelectionRequestRef.current += 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      resetConversationState(activateDraft);
      updateSessionIdInUrl(null);
    },
    [resetConversationState, updateSessionIdInUrl],
  );

  const startNewChat = () => resetActiveConversation(true);

  const clearActiveSession = () => resetActiveConversation(false);

  const selectSession = useCallback(
    (selectedSession: HaygentSession, updateUrl = true) => {
      sessionSelectionRequestRef.current += 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;

      sessionIdRef.current = selectedSession.session_id;
      setSessionId(selectedSession.session_id);
      setHasActiveDraft(false);
      setSessionTitle(selectedSession.name || null);
      setError(null);
      setIsRequestLoading(false);

      if (updateUrl) {
        updateSessionIdInUrl(selectedSession.session_id);
      }
    },
    [updateSessionIdInUrl],
  );

  useEffect(() => {
    if (!sessionsQuery.isSuccess || didRestoreInitialSessionRef.current) {
      return;
    }

    didRestoreInitialSessionRef.current = true;
    const requestedSessionId = new URLSearchParams(location.search).get(CHAT_SESSION_ID_QUERY_PARAM_KEY);
    const initialSession = getInitialHaygentSession(sessionsQuery.data, requestedSessionId);
    if (!initialSession) {
      return;
    }

    const shouldReplaceStaleSessionId = Boolean(requestedSessionId && requestedSessionId !== initialSession.session_id);
    selectSession(initialSession, shouldReplaceStaleSessionId);
  }, [location.search, selectSession, sessionsQuery.data, sessionsQuery.isSuccess]);

  /**
   * Toggles between docked (small) and maximized (large) chat window states.
   */
  const toggleMaximize = () => {
    setChatState((prev) => (prev === HaygentChatState.Maximized ? HaygentChatState.Docked : HaygentChatState.Maximized));
  };

  /**
   * Sends a user message and streams the AI response.
   *
   * Flow:
   * 1. Add user message to history
   * 2. Add empty assistant message placeholder (for streaming)
   * 3. Stream response chunks and append to assistant message
   * 4. Handle errors with user-friendly messages
   */
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) {
        return;
      }

      setError(null);
      setIsRequestLoading(true);
      // Keep the generation active when sending starts so a later session selection can invalidate session creation and stream callbacks.
      const sessionSelectionRequest = sessionSelectionRequestRef.current;

      if (!sessionIdRef.current) {
        try {
          const session = await HaygentService.createSession();
          if (sessionSelectionRequestRef.current !== sessionSelectionRequest) {
            return;
          }
          cacheHaygentSessionMessages(session.session_id, messages);
          sessionIdRef.current = session.session_id;
          setSessionId(session.session_id);
          setHasActiveDraft(false);

          updateSessionIdInUrl(session.session_id);
          refreshHaygentSessions();
        } catch (err) {
          if (sessionSelectionRequestRef.current !== sessionSelectionRequest) {
            return;
          }
          const message = 'Failed to start chat session. Please try again.';
          setError(message);
          // eslint-disable-next-line no-console
          console.error('Failed to create session:', err);
          setIsRequestLoading(false);
          return;
        }
      }

      // Add user message
      const userMessage: HaygentMessage = { role: 'user', content: content.trim() };

      const activeSessionId = sessionIdRef.current;
      if (!activeSessionId) {
        setIsRequestLoading(false);
        return;
      }

      // Prepare messages for API (full history, excluding empty messages)
      const allMessages: HaygentMessage[] = [...messages, userMessage].filter((msg) => msg.content.trim().length > 0);

      // Add placeholder for assistant response
      cacheHaygentSessionMessages(activeSessionId, [...allMessages, { role: 'assistant', content: '' }]);

      // Create abort controller for request cancellation
      abortControllerRef.current = new AbortController();

      // Track if we received any content during streaming
      let receivedContent = '';

      try {
        // Stream chat response via SSE - onChunk callback fires for each received chunk
        await HaygentService.streamChat(
          activeSessionId,
          allMessages,
          (chunk) => {
            if (sessionSelectionRequestRef.current !== sessionSelectionRequest) {
              return;
            }

            if (chunk.title) {
              setSessionTitle((currentTitle) => currentTitle || chunk.title || null);
              updateHaygentSessionName(activeSessionId, chunk.title);
            }

            // Append each chunk to the assistant's message as it arrives
            if (chunk.content) {
              receivedContent += chunk.content;
              // Update the last message (assistant placeholder) with streamed content
              updateHaygentSessionMessages(activeSessionId, (currentMessages) => {
                const updated = [...currentMessages];
                const lastIndex = updated.length - 1;
                if (updated[lastIndex]?.role === 'assistant') {
                  updated[lastIndex] = {
                    ...updated[lastIndex],
                    content: updated[lastIndex].content + chunk.content,
                  };
                }

                return updated;
              });
            }
          },
          abortControllerRef.current.signal,
          fleetUuid,
        );

        if (sessionSelectionRequestRef.current !== sessionSelectionRequest) {
          return;
        }

        // Check if stream completed without any content (empty response)
        if (!receivedContent.trim()) {
          setError('Received empty response. Please try again.');
        } else {
          cacheHaygentSessionMessages(activeSessionId, [...allMessages, { role: 'assistant', content: receivedContent }]);
        }
      } catch (err) {
        if (sessionSelectionRequestRef.current !== sessionSelectionRequest) {
          return;
        }

        // Ignore abort errors - user cancelled the request intentionally
        if ((err as Error).name === 'AbortError') {
          return;
        }

        // Map HTTP status codes to user-friendly error messages
        const { status } = err as Error & { status?: number };
        let errorMessage = 'An error occurred. Please try again.';

        if (status === HttpCodes.UNAUTHORIZED) {
          errorMessage = 'Session expired. Please refresh the page.';
        } else if (status === HttpCodes.TOO_MANY_REQUESTS) {
          errorMessage = 'Too many requests. Please wait a moment.';
        } else if (status === HttpCodes.BAD_GATEWAY) {
          errorMessage = 'Haygent service is temporarily unavailable. Please try again later.';
        } else if (status === HttpCodes.SERVER_ERROR || status === HttpCodes.MAINTENANCE) {
          errorMessage = 'Server error. Please try again later.';
        }

        setError(errorMessage);
      } finally {
        // A stale request must not clear loading state or the controller belonging to a newer conversation.
        if (sessionSelectionRequestRef.current === sessionSelectionRequest) {
          // Clean up empty assistant placeholder if present
          updateHaygentSessionMessages(activeSessionId, (currentMessages) => {
            const last = currentMessages[currentMessages.length - 1];
            if (last?.role === 'assistant' && !last.content) {
              return currentMessages.slice(0, -1);
            }

            return currentMessages;
          });
          setIsRequestLoading(false);
          abortControllerRef.current = null;
        }
      }
    },
    [fleetUuid, messages, updateSessionIdInUrl],
  );

  /**
   * Cancels any ongoing streaming request.
   * Useful when user wants to stop waiting for a response.
   */
  const cancelRequest = () => {
    if (abortControllerRef.current) {
      sessionSelectionRequestRef.current += 1;
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      updateHaygentSessionMessages(sessionIdRef.current, (currentMessages) => {
        const lastMessage = currentMessages[currentMessages.length - 1];
        if (lastMessage?.role === 'assistant' && !lastMessage.content) {
          return currentMessages.slice(0, -1);
        }

        return currentMessages;
      });
      setIsRequestLoading(false);
    }
  };

  return {
    chatState,
    messages,
    sessionTitle,
    isLoading,
    error,
    sessionId,
    hasActiveDraft,
    isHistoryUnavailable,
    availability,
    openChat,
    closeChat,
    startNewChat,
    clearActiveSession,
    selectSession,
    toggleMaximize,
    sendMessage,
    cancelRequest,
  };
}

export function HaygentChatProvider({ children }: React.PropsWithChildren<unknown>) {
  const chat = useHaygentChatState();

  return React.createElement(HaygentChatContext.Provider, { value: chat }, children);
}

export function useHaygentChat(): UseHaygentChatReturn {
  const chat = useContext(HaygentChatContext);
  if (!chat) {
    throw new Error('useHaygentChat must be used within HaygentChatProvider');
  }

  return chat;
}
